import { useState } from "react";
import { StickyNote, Save, Radar, Search, Loader2, Copy, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { patchCandidate, findSocials, generateTagPosts } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { SocialChips } from "@/components/atfq/SocialChips";
import { PostHook, PostHistory } from "./PostMeta";

export const FeatureToggle = ({ c, onChange }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    setBusy(true);
    try {
      onChange(await patchCandidate(c.id, { featured: e.target.checked }));
      toast.success(e.target.checked ? "Added to Voices We're Watching" : "Removed from the public rotation");
    } catch (err) {
      toast.error(extractApiError(err, "Could not update."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="mt-4 flex items-center gap-2 text-sm text-[#0A192F] cursor-pointer" data-testid="scout-feature-label">
      <input
        type="checkbox"
        data-testid="scout-feature-toggle"
        checked={Boolean(c.featured)}
        disabled={busy}
        onChange={toggle}
        className="w-4 h-4 accent-[var(--accent)]"
      />
      <Radar className="w-3.5 h-3.5 text-[var(--accent)]" />
      Feature in &ldquo;Voices We&apos;re Watching&rdquo; (public, rotates Mon/Wed/Fri)
    </label>
  );
};

export const TagPosts = ({ c, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!c.featured) return null;

  const gen = async () => {
    setBusy(true);
    try {
      const { tag_posts } = await generateTagPosts(c.id);
      const history = c.tag_posts?.x ? [...(c.post_history || []), c.tag_posts] : c.post_history;
      onChange({ ...c, tag_posts, post_history: history });
      toast.success("Fresh posts ready");
    } catch (err) {
      toast.error(extractApiError(err, "Could not write posts."));
    } finally {
      setBusy(false);
    }
  };

  const Post = ({ label, text, intent }) => (
    <div className="border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label} · {text.length} chars</span>
        <div className="flex gap-2">
          <button type="button" data-testid={`scout-tagpost-copy-${label.toLowerCase()}`} onClick={() => copyText(text).then(() => toast.success("Copied"))} className="text-xs text-slate-500 hover:text-[#0A192F] inline-flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copy
          </button>
          <a href={intent} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--accent)] hover:text-[#0A192F]">Open →</a>
        </div>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{text}</p>
    </div>
  );

  return (
    <div className="mt-4" data-testid="scout-tagposts">
      {c.tag_posts?.x ? (
        <div className="space-y-2">
          <PostHook posts={c.tag_posts} />
          <Post label="X" text={c.tag_posts.x} intent={`https://x.com/intent/post?text=${encodeURIComponent(c.tag_posts.x)}`} />
          <Post label="Bluesky" text={c.tag_posts.bluesky} intent={`https://bsky.app/intent/compose?text=${encodeURIComponent(c.tag_posts.bluesky)}`} />
          <button type="button" data-testid="scout-tagposts-rewrite" onClick={gen} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:text-[#0A192F] disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Megaphone className="w-3 h-3" />}
            {busy ? "Searching the news…" : "Rewrite with fresh angle"}
          </button>
          <PostHistory history={c.post_history} />
        </div>
      ) : (
        <button
          type="button"
          data-testid="scout-tagposts-generate"
          onClick={gen}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] disabled:opacity-50 px-3 py-2 text-xs font-medium transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
          {busy ? "Writing X + Bluesky posts…" : "Write tag posts (X + Bluesky)"}
        </button>
      )}
    </div>
  );
};

const SocialsToolbar = ({ c, busy, handles, onLookup }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="text-xs text-slate-500">Social handles</div>
    <button
      type="button"
      data-testid="scout-socials-find"
      onClick={onLookup}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:text-[#0A192F] disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
      {busy ? "Searching…" : c.socials?.length ? "Re-check" : "Find socials"}
    </button>
    {handles && (
      <button
        type="button"
        data-testid="scout-socials-copy"
        onClick={() => copyText(handles).then(() => toast.success("Handles copied"))}
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0A192F]"
      >
        <Copy className="w-3 h-3" /> Copy all
      </button>
    )}
  </div>
);

export const SocialsPanel = ({ c, onChange }) => {
  const [busy, setBusy] = useState(false);
  const lookup = async () => {
    setBusy(true);
    try {
      const updated = await findSocials(c.id);
      onChange(updated);
      toast.success(updated.socials?.length ? `Found ${updated.socials.length} profiles` : "No verified profiles found");
    } catch (err) {
      toast.error(extractApiError(err, "Lookup failed."));
    } finally {
      setBusy(false);
    }
  };
  const handles = (c.socials || []).map((s) => s.handle || s.url).join(" ");
  return (
    <div className="mt-4" data-testid="scout-socials">
      <SocialsToolbar c={c} busy={busy} handles={handles} onLookup={lookup} />
      <SocialChips socials={c.socials} className="mt-2" />
      {!c.socials?.length && c.socials_checked_at && (
        <div className="mt-1 text-xs text-slate-400">Checked — nothing verified yet.</div>
      )}
    </div>
  );
};

export const CandidateNotes = ({ c, onChange }) => {
  const [open, setOpen] = useState(Boolean(c.notes));
  const [notes, setNotes] = useState(c.notes || "");
  const [busy, setBusy] = useState(false);
  const dirty = notes !== (c.notes || "");

  const save = async () => {
    setBusy(true);
    try {
      onChange(await patchCandidate(c.id, { notes }));
      toast.success("Note saved");
    } catch (err) {
      toast.error(extractApiError(err, "Could not save note."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <button
        type="button"
        data-testid="scout-notes-toggle"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#0A192F] transition-colors"
      >
        <StickyNote className="w-3.5 h-3.5" />
        {c.notes ? "Private notes" : "Add a private note"}
        {c.notes && !open && <span className="text-slate-400 truncate max-w-[280px]">— {c.notes}</span>}
      </button>
      {open && (
        <div className="mt-2">
          <textarea
            data-testid="scout-notes-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Only you see this — angles, hesitations, who introduced you, follow-up dates…"
            className="w-full border border-slate-300 bg-amber-50/40 px-3 py-2 text-sm outline-none focus:border-[var(--accent)] resize-y"
          />
          <button
            type="button"
            data-testid="scout-notes-save"
            onClick={save}
            disabled={!dirty || busy}
            className="mt-2 inline-flex items-center gap-2 border border-[#0A192F] hover:bg-[#0A192F] hover:text-white disabled:opacity-40 px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save note"}
          </button>
        </div>
      )}
    </div>
  );
};
